import { startOfDay } from 'date-fns';
import type { EpcProjectOrgUnit } from '../../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { orgUnitName, taskOrgUnitId } from './epcScheduleDepartmentAssignment';
import { parseTaskDay } from './epcScheduleDatesDisplay';
import {
  parsePredecessorLinks,
  parseSuccessorLinks,
  predecessorDisplayLabel,
  resolvePredecessorTaskIndex,
  resolveSuccessorTaskIndex,
  successorDisplayLabel,
} from './epcScheduleDependencies';
import { taskPercent } from './taskStatus';

export type DependencyNodeTone = 'done' | 'run' | 'idle' | 'blocked';

export type DependencyNodeVm = {
  /** Row index of the linked task; null when the link could not be resolved. */
  targetIndex: number | null;
  label: string;
  idLabel: string;
  linkBadge: string;
  tone: DependencyNodeTone;
  statusLine: string;
  deptName: string;
};

export function formatLinkBadge(code: string, lag?: string): string {
  if (!lag) return code;
  const signed = /^[+-]/.test(lag) ? lag : `+${lag}`;
  return `${code} ${signed}`;
}

function idLabelAt(tasks: EpcScheduleTaskRow[], idx: number | null): string {
  if (idx == null) return '';
  const id = tasks[idx]?.id;
  return id != null && id !== '' ? `#${id}` : `row ${idx + 1}`;
}

function plainStatus(target: EpcScheduleTaskRow | undefined): { tone: DependencyNodeTone; statusLine: string } {
  if (!target) return { tone: 'idle', statusLine: '○ Unresolved' };
  const pct = taskPercent(target);
  if (pct >= 100) return { tone: 'done', statusLine: '✓ Done' };
  if (pct > 0) return { tone: 'run', statusLine: `● In progress ${Math.round(pct)}%` };
  return { tone: 'idle', statusLine: '○ Not started' };
}

/** Left column of the dependency map — tasks this row waits on. */
export function buildUpstreamNodes(
  tasks: EpcScheduleTaskRow[],
  index: number,
  orgUnits: EpcProjectOrgUnit[]
): DependencyNodeVm[] {
  const task = tasks[index];
  if (!task) return [];
  return parsePredecessorLinks(task.predecessors).map((link) => {
    const ti = resolvePredecessorTaskIndex(link, tasks, index);
    const target = ti != null ? tasks[ti] : undefined;
    const { tone, statusLine } = plainStatus(target);
    return {
      targetIndex: ti,
      label: predecessorDisplayLabel(link, tasks, index),
      idLabel: idLabelAt(tasks, ti),
      linkBadge: formatLinkBadge(link.linkTypeCode, link.lag),
      tone,
      statusLine,
      deptName: target ? orgUnitName(orgUnits, taskOrgUnitId(target)) : '',
    };
  });
}

/** Right column — tasks waiting on this row. Blocked when FS, this row < 100%, successor 0% and its start has arrived. */
export function buildDownstreamNodes(
  tasks: EpcScheduleTaskRow[],
  index: number,
  orgUnits: EpcProjectOrgUnit[],
  today: Date
): DependencyNodeVm[] {
  const task = tasks[index];
  if (!task) return [];
  const day = startOfDay(today);
  const thisPct = taskPercent(task);
  return parseSuccessorLinks(task.successors).map((link) => {
    const si = resolveSuccessorTaskIndex(link, tasks, index);
    const target = si != null ? tasks[si] : undefined;
    let { tone, statusLine } = plainStatus(target);
    if (target && link.linkTypeCode === 'FS' && thisPct < 100 && taskPercent(target) <= 0) {
      const succStart = parseTaskDay(target.start);
      if (succStart && startOfDay(succStart) <= day) {
        tone = 'blocked';
        statusLine = '■ Blocked';
      }
    }
    return {
      targetIndex: si,
      label: successorDisplayLabel(link, tasks, index),
      idLabel: idLabelAt(tasks, si),
      linkBadge: formatLinkBadge(link.linkTypeCode, link.lag),
      tone,
      statusLine,
      deptName: target ? orgUnitName(orgUnits, taskOrgUnitId(target)) : '',
    };
  });
}
